import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import AuthHeader from "../components/AuthHeader";
import {
  Container,
  Typography,
  Box,
  Button,
  Stack,
  Card,
  CardContent,
  LinearProgress,
} from "@mui/material";

function HomeworkPage({ API_BASE }) {
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  const [homeworkList, setHomeworkList] = useState([]);
  const [selectedHomework, setSelectedHomework] = useState(null);
  const [wordCards, setWordCards] = useState([]);
  const [quizMode, setQuizMode] = useState(false);
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [score, setScore] = useState(0);
  const [selectedAnswer, setSelectedAnswer] = useState(null);
  const [showResult, setShowResult] = useState(false);
  const [quizFinished, setQuizFinished] = useState(false);
  const [currentShuffledChoices, setCurrentShuffledChoices] = useState([]);
  const [correctChoiceIndex, setCorrectChoiceIndex] = useState(-1);

  // ログイン状態チェック
  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/login', { state: { from: '/homework' }, replace: true });
    }
  }, [isAuthenticated, navigate]);
  useEffect(() => {
    fetchHomework();
  }, []);

  useEffect(() => {
    // When wordCards are loaded and quiz is starting, prepare the first question
    if (wordCards.length > 0 && quizMode && currentQuestion === 0 && currentShuffledChoices.length === 0) {
      prepareQuestion(0);
    }
  }, [wordCards, quizMode, currentQuestion, currentShuffledChoices.length]);

  const fetchHomework = async () => {
    try {
      const response = await fetch(`${API_BASE}/homework`);
      const data = await response.json();
      setHomeworkList(data);
    } catch (error) {
      console.error("Error fetching homework:", error);
    }
  };

  const fetchWordCards = async (homeworkId) => {
    try {
      const response = await fetch(`${API_BASE}/homework/${homeworkId}/cards`);
      const data = await response.json();
      setWordCards(data);
      setSelectedHomework(homeworkId);
    } catch (error) {
      console.error("Error fetching word cards:", error);
    }
  };

  const startQuiz = () => {
    setQuizMode(true);
    setCurrentQuestion(0);
    setScore(0);
    setSelectedAnswer(null);
    setShowResult(false);
    setQuizFinished(false);
    // Shuffle choices for the first question
    if (wordCards.length > 0) {
      prepareQuestion(0);
    }
  };

  const prepareQuestion = (questionIndex) => {
    if (questionIndex < wordCards.length) {
      const currentCard = wordCards[questionIndex];
      const shuffled = shuffleChoices(currentCard.choices);
      const correctIndex = shuffled.findIndex(choice => choice.choice_type === 'correct');
      
      setCurrentShuffledChoices(shuffled);
      setCorrectChoiceIndex(correctIndex);
    }
  };

  const handleAnswerClick = (choiceIndex) => {
    if (selectedAnswer !== null) return; // Already answered
    
    setSelectedAnswer(choiceIndex);
    setShowResult(true);
    
    // Check if correct using the stored correct index
    if (choiceIndex === correctChoiceIndex) {
      setScore(score + 1);
    }
    
    // Move to next question after 1 second
    setTimeout(() => {
      if (currentQuestion + 1 >= wordCards.length) {
        // Quiz finished
        setQuizFinished(true);
      } else {
        // Next question
        const nextQuestion = currentQuestion + 1;
        setCurrentQuestion(nextQuestion);
        setSelectedAnswer(null);
        setShowResult(false);
        // Prepare the next question's choices
        prepareQuestion(nextQuestion);
      }
    }, 1000);
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString("ja-JP", {
      year: "numeric",
      month: "numeric",
      day: "numeric",
    });
  };

  const handleHomeworkClick = (homework) => {
    fetchWordCards(homework.id);
  };

  const handleBackClick = () => {
    setSelectedHomework(null);
    setWordCards([]);
    setQuizMode(false);
    setQuizFinished(false);
    setCurrentShuffledChoices([]);
    setCorrectChoiceIndex(-1);
  };

  const shuffleChoices = (choices) => {
    // Shuffle the choices array while keeping track of correct answer
    const shuffled = [...choices];
    for (let i = shuffled.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
    }
    return shuffled;
  };

  // ログイン中のみ表示
  if (!isAuthenticated) {
    return null; // リダイレクト中は何も表示しない
  }
  if (selectedHomework) {
    // Quiz finished - show results
    if (quizFinished) {
      return (
        <Container maxWidth="md" sx={{ paddingTop: 4, paddingBottom: 4 }}>
          <Box sx={{ textAlign: 'center' }}>
            <Typography 
              variant="h3" 
              component="h1" 
              gutterBottom 
              sx={{ fontWeight: 'bold', marginBottom: 4 }}
            >
              🎉 クイズ完了！
            </Typography>
            
            <Card sx={{ maxWidth: 400, margin: '0 auto', padding: 4 }}>
              <CardContent>
                <Typography variant="h4" color="primary" sx={{ marginBottom: 2 }}>
                  {score} / {wordCards.length}
                </Typography>
                <Typography variant="h6" sx={{ marginBottom: 3 }}>
                  正解率: {Math.round((score / wordCards.length) * 100)}%
                </Typography>
                
                <Stack spacing={2}>
                  <Button 
                    variant="contained" 
                    size="large"
                    onClick={startQuiz}
                  >
                    もう一度挑戦
                  </Button>
                  <Button 
                    variant="outlined" 
                    onClick={handleBackClick}
                  >
                    宿題一覧に戻る
                  </Button>
                </Stack>
              </CardContent>
            </Card>
          </Box>
        </Container>
      );
    }
    
    // Quiz mode
    if (quizMode && wordCards.length > 0 && currentShuffledChoices.length > 0) {
      const currentCard = wordCards[currentQuestion];
      
      return (
        <Container maxWidth="md" sx={{ paddingTop: 4, paddingBottom: 4 }}>
          <Box sx={{ marginBottom: 4 }}>
            <Button 
              variant="outlined" 
              onClick={handleBackClick}
              sx={{ marginBottom: 2 }}
            >
              ← 戻る
            </Button>
            
            <Typography variant="h6" align="center" sx={{ marginBottom: 2 }}>
              問題 {currentQuestion + 1} / {wordCards.length}
            </Typography>
            
            <LinearProgress 
              variant="determinate" 
              value={(currentQuestion / wordCards.length) * 100}
              sx={{ marginBottom: 3 }}
            />
          </Box>
          
          <Card sx={{ maxWidth: 600, margin: '0 auto', padding: 4 }}>
            <CardContent>
              <Typography 
                variant="h4" 
                component="h2" 
                align="center"
                sx={{ 
                  fontWeight: 'bold', 
                  marginBottom: 1,
                  color: 'primary.main'
                }}
              >
                {currentCard.word_jp}
              </Typography>
              
              <Typography 
                variant="body1" 
                align="center"
                color="text.secondary"
                sx={{ marginBottom: 4 }}
              >
                ({currentCard.pos})
              </Typography>
              
              <Stack spacing={2}>
                {currentShuffledChoices.map((choice, index) => {
                  let buttonColor = 'inherit';
                  let backgroundColor = 'transparent';
                  
                  if (showResult && selectedAnswer === index) {
                    if (index === correctChoiceIndex) {
                      buttonColor = 'success';
                      backgroundColor = 'success.light';
                    } else {
                      buttonColor = 'error';
                      backgroundColor = 'error.light';
                    }
                  } else if (showResult && index === correctChoiceIndex) {
                    buttonColor = 'success';
                    backgroundColor = 'success.light';
                  }
                  
                  return (
                    <Button
                      key={index}
                      variant="outlined"
                      size="large"
                      onClick={() => handleAnswerClick(index)}
                      disabled={selectedAnswer !== null}
                      sx={{
                        padding: 2,
                        fontSize: '1.2rem',
                        fontFamily: 'monospace',
                        textTransform: 'none',
                        color: buttonColor,
                        backgroundColor: backgroundColor,
                        borderColor: buttonColor !== 'inherit' ? buttonColor : undefined,
                        '&:hover': {
                          backgroundColor: selectedAnswer === null ? 'action.hover' : backgroundColor,
                        }
                      }}
                    >
                      {choice.choice_word}
                    </Button>
                  );
                })}
              </Stack>
              
              {showResult && (
                <Box sx={{ textAlign: 'center', marginTop: 3 }}>
                  <Typography 
                    variant="h6" 
                    color={selectedAnswer === correctChoiceIndex ? 'success.main' : 'error.main'}
                  >
                    {selectedAnswer === correctChoiceIndex ? '✅ 正解！' : '❌ 不正解'}
                  </Typography>
                  {selectedAnswer !== correctChoiceIndex && (
                    <Typography variant="body2" color="text.secondary" sx={{ marginTop: 1 }}>
                      正解: {currentShuffledChoices[correctChoiceIndex].choice_word}
                    </Typography>
                  )}
                </Box>
              )}
            </CardContent>
          </Card>
        </Container>
      );
    }
    
    // Card view mode (before starting quiz)
    return (
      <Container maxWidth="md" sx={{ paddingTop: 4, paddingBottom: 4 }}>
        <Box sx={{ marginBottom: 4 }}>
          <Button 
            variant="outlined" 
            onClick={handleBackClick}
            sx={{ marginBottom: 2 }}
          >
            ← 戻る
          </Button>
          <Typography 
            variant="h4" 
            component="h1" 
            gutterBottom 
            align="center"
            sx={{ fontWeight: 'bold' }}
          >
            📝 単語クイズ
          </Typography>
        </Box>
        
        <Card sx={{ maxWidth: 400, margin: '0 auto', padding: 4 }}>
          <CardContent sx={{ textAlign: 'center' }}>
            <Typography variant="h6" sx={{ marginBottom: 3 }}>
              {wordCards.length}問のクイズがあります
            </Typography>
            
            <Typography variant="body2" color="text.secondary" sx={{ marginBottom: 4 }}>
              日本語の意味に対応する正しい英単語を選んでください。
            </Typography>
            
            <Button 
              variant="contained" 
              size="large"
              onClick={startQuiz}
              sx={{ width: '100%' }}
            >
              クイズを開始
            </Button>
          </CardContent>
        </Card>
        
        {wordCards.length === 0 && (
          <Typography variant="body1" align="center" color="text.secondary">
            単語データがありません
          </Typography>
        )}
      </Container>
    );
  }

  return (
    <>
      <AuthHeader />
      <Container maxWidth="md" sx={{ paddingTop: 4, paddingBottom: 4 }}>
      <Typography 
        variant="h3" 
        component="h1" 
        gutterBottom 
        align="center"
        sx={{ fontWeight: 'bold', marginBottom: 4 }}
      >
        📚 宿題
      </Typography>
      
      <Box sx={{ marginTop: 4 }}>
        <Stack spacing={2}>
          {homeworkList.map((homework) => (
            <Button
              key={homework.id}
              variant="outlined"
              size="large"
              onClick={() => handleHomeworkClick(homework)}
              sx={{
                padding: 2,
                fontSize: '1.2rem',
                fontWeight: 'bold',
                textAlign: 'left',
                justifyContent: 'flex-start',
                '&:hover': {
                  backgroundColor: 'primary.light',
                  color: 'white',
                }
              }}
            >
              {formatDate(homework.processed_at)}
            </Button>
          ))}
        </Stack>
        
        {homeworkList.length === 0 && (
          <Typography variant="body1" align="center" color="text.secondary">
            宿題データがありません
          </Typography>
        )}
      </Box>
    </Container>
    </>
  );
}

export default HomeworkPage;
